import {useMemo} from 'react';

import {apiClient} from 'common/util/api-client';

import {Canvas} from '../types';

type UseCanvasApiProps = {
  projectId: number;
  workspaceId: number;
};

// Request types
export type CreateCanvasRequest = {
  name: string;
  description?: string;
};
type DuplicateCanvasRequest = {id: string};
export type UpdateCanvasRequest = {
  id: string;
  name: string;
  description?: string;
};
type GetCanvasRequest = {id: string};
type DeleteCanvasRequest = {id: string};
type PinCanvasRequest = {
  id: string;
  isPinned: boolean;
};
type FavoriteCanvasRequest = {
  id: string;
  isFavorited: boolean;
};

// Response types
export type DeleteCanvasResponse = {id: string};
type GetCanvasResponse = Canvas;
type PinCanvasResponse = null;
type FavoriteCanvasResponse = null;

export function useCanvasApi({projectId, workspaceId}: UseCanvasApiProps) {
  const basePath = useMemo(() => {
    const parentEntityPath = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
    return `/api/app/${parentEntityPath}/canvases`;
  }, [workspaceId, projectId]);

  function createCanvas({name, description}: CreateCanvasRequest) {
    return apiClient.post<Canvas>(basePath, {
      name,
      description,
    });
  }

  function duplicateCanvas({id}: DuplicateCanvasRequest) {
    return apiClient.post<Canvas>(`${basePath}/${id}/duplicate`, {});
  }

  function updateCanvas({id, name, description}: UpdateCanvasRequest) {
    return apiClient.patch<Canvas>(`${basePath}/${id}`, {
      name,
      description,
    });
  }

  function deleteCanvas({id}: DeleteCanvasRequest) {
    return apiClient.delete<DeleteCanvasResponse>(`${basePath}/${id}`);
  }

  function getCanvas({id}: GetCanvasRequest) {
    return apiClient.get<GetCanvasResponse>(`${basePath}/${id}`);
  }

  function pinCanvas({id, isPinned}: PinCanvasRequest) {
    if (isPinned) {
      return apiClient.post<PinCanvasResponse>(`${basePath}/${id}/pin`, {});
    }
    return apiClient.delete<PinCanvasResponse>(`${basePath}/${id}/pin`);
  }

  function favoriteCanvas({id, isFavorited}: FavoriteCanvasRequest) {
    if (isFavorited) {
      return apiClient.post<FavoriteCanvasResponse>(`${basePath}/${id}/favorites`, {});
    }
    return apiClient.delete<FavoriteCanvasResponse>(`${basePath}/${id}/favorites`);
  }

  return {
    createCanvas,
    duplicateCanvas,
    updateCanvas,
    deleteCanvas,
    getCanvas,
    pinCanvas,
    favoriteCanvas,
  };
}
